import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Title, Text, TextInput, Select, SelectItem, Button, Badge } from '@tremor/react';
import { api } from '../lib/api';
import { useSocket } from '../hooks/useSocket';
import { StartBatchModal } from '../components/modals/StartBatchModal';
import type { DocumentListItem, BatchesResponse } from '../types';

const PAGE_SIZE = 50;

// Column configuration
type ColumnKey = 'fileName' | 'fileType' | 'size' | 'pages' | 'sentences' | 'confidence' | 'genre' | 'labels' | 'annotations' | 'status' | 'updated';

interface ColumnConfig {
  key: ColumnKey;
  label: string;
  sortField?: string;
  defaultVisible: boolean;
}

const COLUMNS: ColumnConfig[] = [
  { key: 'fileName', label: 'Filename', defaultVisible: true },
  { key: 'fileType', label: 'File Type', defaultVisible: false },
  { key: 'size', label: 'Size', sortField: 'fetchResult.fileSize', defaultVisible: false },
  { key: 'pages', label: 'Pages', sortField: 'extractResult.extractedPagesCount', defaultVisible: true },
  { key: 'sentences', label: 'Sentences', sortField: 'evaluationResult.sentenceCount', defaultVisible: false },
  { key: 'confidence', label: 'Confidence', sortField: 'evaluationResult.extractionConfidence', defaultVisible: false },
  { key: 'genre', label: 'Genre', sortField: 'evaluationResult.genreLabel', defaultVisible: true },
  { key: 'labels', label: 'Labels', sortField: 'evaluationResult.harmfulCount', defaultVisible: true },
  { key: 'annotations', label: 'Annotations', sortField: 'annotationResult.highlightedCount', defaultVisible: true },
  { key: 'status', label: 'Status', sortField: 'status', defaultVisible: true },
  { key: 'updated', label: 'Updated', sortField: 'updatedAt', defaultVisible: true },
];

const STORAGE_KEY = 'documentListColumns';
const STORAGE_VERSION_KEY = 'documentListColumnsVersion';
// Bump this when adding new defaultVisible columns to force them into existing users' saved sets
const COLUMNS_VERSION = 2; // v2: added 'genre' column

function getDefaultVisibleColumns(): Set<ColumnKey> {
  return new Set(COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
}

function loadVisibleColumns(): Set<ColumnKey> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ColumnKey[];
      const columns = new Set(parsed);

      // Migrate: inject any new defaultVisible columns added since last version
      const savedVersion = parseInt(localStorage.getItem(STORAGE_VERSION_KEY) || '1', 10);
      if (savedVersion < COLUMNS_VERSION) {
        for (const col of COLUMNS) {
          if (col.defaultVisible && !columns.has(col.key)) {
            columns.add(col.key);
          }
        }
        saveVisibleColumns(columns);
      }

      return columns;
    }
  } catch {
    // Ignore parse errors
  }
  return getDefaultVisibleColumns();
}

function saveVisibleColumns(columns: Set<ColumnKey>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...columns]));
  localStorage.setItem(STORAGE_VERSION_KEY, String(COLUMNS_VERSION));
}

// Convert MIME type to friendly display name
function formatFileType(mimeType: string | null, originalFileType: string | null): string {
  const type = originalFileType || mimeType;
  if (!type) return '--';

  const mimeMap: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/msword': 'Word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/vnd.ms-powerpoint': 'PowerPoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
    'text/plain': 'Text',
    'text/html': 'HTML',
    'text/csv': 'CSV',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/tiff': 'TIFF',
    'message/rfc822': 'Email',
    'application/vnd.ms-outlook': 'Outlook',
  };

  return mimeMap[type] || type.split('/').pop()?.toUpperCase() || type;
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return '--';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function buildLabelTooltip(
  labelCounts: Record<string, number>,
  backends: Record<string, string>,
  harmfulCount: number,
): string {
  const totalLabels = Object.values(labelCounts).reduce((sum, n) => sum + n, 0);
  const summary = totalLabels > 0
    ? `${totalLabels} labels across ${harmfulCount} sentences`
    : `${harmfulCount} sentences`;

  const breakdown = Object.entries(labelCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([compositeKey, count]) => {
      const [classifierName] = compositeKey.includes(':')
        ? [compositeKey.split(':')[0]]
        : [null];
      const backend = classifierName ? backends[classifierName] : undefined;
      const tag = backend === 'phi' ? '[Phi]' : backend === 'openai' ? '[OpenAI]' : '';
      return `  ${tag ? tag + ' ' : ''}${compositeKey} (${count})`;
    })
    .join('\n');

  return breakdown ? `${summary}\n${breakdown}` : summary;
}

function formatGenreDisplay(genre: string): string {
  const specialLabels: Record<string, string> = {
    'empty': 'Empty Document',
    'error': 'Classification Error',
    'unknown': 'Unknown',
  };
  if (specialLabels[genre]) return specialLabels[genre];
  return genre.replace(/_/g, ' ');
}

function getStatusColor(status: string): 'emerald' | 'blue' | 'red' | 'yellow' | 'gray' | 'orange' {
  switch (status) {
    case 'completed': return 'emerald';
    case 'processing': return 'blue';
    case 'failed': return 'red';
    case 'pending': return 'yellow';
    case 'skipped': return 'orange';
    default: return 'gray';
  }
}

export function DocumentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State from URL params
  const currentPage = parseInt(searchParams.get('page') || '1');
  const searchQuery = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || '';
  const batchFilter = searchParams.get('batch') || '';
  const fileTypeFilter = searchParams.get('fileType') || '';
  const genreFilter = searchParams.get('genre') || '';
  const sortParam = searchParams.get('sort') || '-updatedAt';

  // Local state
  const [documents, setDocuments] = useState<DocumentListItem[]>([]);
  const [pagination, setPagination] = useState({ total: 0, hasMore: false });
  const [batches, setBatches] = useState<string[]>([]);
  const [fileTypes, setFileTypes] = useState<string[]>([]);
  const [genres, setGenres] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [classifierBackends, setClassifierBackends] = useState<Record<string, string>>({});

  // Local input state (for debouncing search)
  const [searchInput, setSearchInput] = useState(searchQuery);

  // Upload modal
  const [showUpload, setShowUpload] = useState(false);

  // Export state
  const [exportState, setExportState] = useState<{
    status: 'idle' | 'processing' | 'completed' | 'failed';
    exportId?: string;
    totalDocuments?: number;
    processedDocuments?: number;
    rowCount?: number;
    fileName?: string;
    error?: string;
  }>({ status: 'idle' });
  const { subscribe } = useSocket();

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(() => loadVisibleColumns());
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef<HTMLDivElement>(null);

  // Close column picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (columnPickerRef.current && !columnPickerRef.current.contains(event.target as Node)) {
        setShowColumnPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listen for background export progress/completion/failure via WebSocket
  useEffect(() => {
    const unsubProgress = subscribe('export:progress', (data) => {
      setExportState((prev) => {
        if (prev.exportId !== data.exportId) return prev;
        return { ...prev, processedDocuments: data.processedDocuments, rowCount: data.rowCount };
      });
    });
    const unsubComplete = subscribe('export:complete', (data) => {
      setExportState((prev) => {
        if (prev.exportId !== data.exportId) return prev;
        // Auto-trigger download
        api.downloadExport(data.exportId).catch(() => {});
        return { status: 'idle' };
      });
    });
    const unsubFailed = subscribe('export:failed', (data) => {
      setExportState((prev) => {
        if (prev.exportId !== data.exportId) return prev;
        return { status: 'failed', error: data.error };
      });
    });
    return () => { unsubProgress(); unsubComplete(); unsubFailed(); };
  }, [subscribe]);

  const toggleColumn = (key: ColumnKey) => {
    const newColumns = new Set(visibleColumns);
    if (newColumns.has(key)) {
      // Don't allow hiding all columns - keep at least fileName
      if (newColumns.size > 1 || key === 'fileName') {
        newColumns.delete(key);
      }
    } else {
      newColumns.add(key);
    }
    setVisibleColumns(newColumns);
    saveVisibleColumns(newColumns);
  };

  const isColumnVisible = (key: ColumnKey) => visibleColumns.has(key);

  // Fetch batches and file types for filter dropdowns
  useEffect(() => {
    api.getBatches()
      .then((response: BatchesResponse) => {
        const batchNames = response.batches
          .map(b => b.name)
          .filter((name): name is string => name !== null && name !== 'Unknown');
        setBatches(batchNames);
      })
      .catch(() => {
        // Ignore - batches filter will just be empty
      });

    api.getFileTypes()
      .then((response) => {
        setFileTypes(response.fileTypes);
      })
      .catch(() => {
        // Ignore - file types filter will just be empty
      });

    api.getGenres()
      .then((response) => {
        setGenres(response.genres);
      })
      .catch(() => {
        // Ignore - genres filter will just be empty
      });
  }, []);

  // Fetch documents (with AbortController to cancel stale requests)
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchDocuments = useCallback(async () => {
    // Cancel any in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const skip = (currentPage - 1) * PAGE_SIZE;
      const response = await api.getDocuments({
        limit: PAGE_SIZE,
        skip,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        batchName: batchFilter || undefined,
        fileType: fileTypeFilter || undefined,
        genre: genreFilter || undefined,
        sort: sortParam,
      });
      // Only update state if this request wasn't aborted
      if (!controller.signal.aborted) {
        setDocuments(response.documents);
        setPagination({
          total: response.pagination.total,
          hasMore: response.pagination.hasMore,
        });
        if (response.classifierBackends) {
          setClassifierBackends(response.classifierBackends);
        }
      }
    } catch (err) {
      // Ignore aborted requests
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [currentPage, searchQuery, statusFilter, batchFilter, fileTypeFilter, genreFilter, sortParam]);

  useEffect(() => {
    fetchDocuments();
    return () => { abortControllerRef.current?.abort(); };
  }, [fetchDocuments]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        updateParams({ search: searchInput, page: '1' });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  // Update URL params
  const updateParams = (updates: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Sort handling
  const handleSort = (field: string) => {
    let newSort: string;
    if (sortParam === field) {
      // Currently ascending, switch to descending
      newSort = `-${field}`;
    } else if (sortParam === `-${field}`) {
      // Currently descending, switch to default (updatedAt desc)
      newSort = '-updatedAt';
    } else {
      // Not sorted by this field, sort ascending
      newSort = field;
    }
    updateParams({ sort: newSort, page: '1' });
  };

  const getSortIndicator = (field: string) => {
    if (sortParam === field) return ' ↑';
    if (sortParam === `-${field}`) return ' ↓';
    return '';
  };

  const handleRowClick = (documentId: string) => {
    // Store document IDs in sessionStorage for Prev/Next navigation
    const documentIds = documents.map(d => d.documentId);
    sessionStorage.setItem('documentNavList', JSON.stringify(documentIds));
    sessionStorage.setItem('documentNavIndex', String(documentIds.indexOf(documentId)));
    navigate(`/documents/${encodeURIComponent(documentId)}`);
  };

  const handleExport = async () => {
    setExportState({ status: 'processing' });
    try {
      const { exportId, totalDocuments, fileName } = await api.startCsvExport({
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        batchName: batchFilter || undefined,
        fileType: fileTypeFilter || undefined,
      });
      setExportState({ status: 'processing', exportId, totalDocuments, processedDocuments: 0, rowCount: 0, fileName });
    } catch (err) {
      setExportState({ status: 'failed', error: err instanceof Error ? err.message : 'Export failed' });
    }
  };

  const totalPages = Math.ceil(pagination.total / PAGE_SIZE);

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      <Card className="bg-gray-900 ring-gray-800">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-baseline gap-3">
            <Title className="text-gray-100">Documents</Title>
            {!isLoading && (
              <Text className="text-gray-400 text-sm">
                {pagination.total.toLocaleString()} {pagination.total === 1 ? 'result' : 'results'}
              </Text>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <TextInput
              placeholder="Search by filename or ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full sm:w-64"
            />
            {/* Status filter */}
            <Select
              value={statusFilter}
              onValueChange={(value) => updateParams({ status: value, page: '1' })}
              placeholder="All statuses"
              className="w-full sm:w-40 bg-gray-800 border-gray-700"
            >
              <SelectItem value="">All statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
            </Select>
            {/* Batch filter */}
            <Select
              value={batchFilter}
              onValueChange={(value) => updateParams({ batch: value, page: '1' })}
              placeholder="All batches"
              className="w-full sm:w-48 bg-gray-800 border-gray-700"
            >
              <SelectItem value="">All batches</SelectItem>
              {batches.map((batch) => (
                <SelectItem key={batch} value={batch}>
                  {batch}
                </SelectItem>
              ))}
            </Select>
            {/* File type filter */}
            <Select
              value={fileTypeFilter}
              onValueChange={(value) => updateParams({ fileType: value, page: '1' })}
              placeholder="All types"
              className="w-full sm:w-40 bg-gray-800 border-gray-700"
            >
              <SelectItem value="">All types</SelectItem>
              {fileTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {formatFileType(type, null)}
                </SelectItem>
              ))}
            </Select>
            {/* Genre filter */}
            <Select
              value={genreFilter}
              onValueChange={(value) => updateParams({ genre: value, page: '1' })}
              placeholder="All genres"
              className="w-full sm:w-48 bg-gray-800 border-gray-700"
            >
              <SelectItem value="">All genres</SelectItem>
              {genres.map((genre) => (
                <SelectItem key={genre} value={genre}>
                  {formatGenreDisplay(genre)}
                </SelectItem>
              ))}
            </Select>
            {/* Upload button */}
            <Button
              size="xs"
              variant="secondary"
              onClick={() => setShowUpload(true)}
              className="bg-gray-800 border-gray-700 whitespace-nowrap"
            >
              Upload
            </Button>
            {/* Export button */}
            <Button
              size="xs"
              variant="secondary"
              onClick={exportState.status === 'failed' ? () => setExportState({ status: 'idle' }) : handleExport}
              disabled={exportState.status === 'processing'}
              className="bg-gray-800 border-gray-700 whitespace-nowrap"
            >
              {exportState.status === 'processing'
                ? exportState.totalDocuments
                  ? `Exporting... ${exportState.processedDocuments ?? 0}/${exportState.totalDocuments} docs`
                  : 'Starting export...'
                : exportState.status === 'failed'
                  ? 'Export failed - Retry?'
                  : 'Export CSV'}
            </Button>
            {/* Column picker */}
            <div className="relative inline-flex" ref={columnPickerRef}>
              <Button
                size="xs"
                variant="secondary"
                onClick={() => setShowColumnPicker(!showColumnPicker)}
                className="bg-gray-800 border-gray-700"
                title="Choose columns"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </Button>
              {showColumnPicker && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    <div className="px-3 py-1 text-xs font-medium text-gray-400 uppercase">Columns</div>
                    {COLUMNS.map(col => (
                      <label
                        key={col.key}
                        className="flex items-center px-3 py-1.5 hover:bg-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns.has(col.key)}
                          onChange={() => toggleColumn(col.key)}
                          className="mr-2 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
                        />
                        <span className="text-sm text-gray-300">{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-red-950/50 border border-red-900 rounded-lg p-4 mb-4">
            <Text className="text-red-400">{error}</Text>
          </div>
        )}

        {/* Loading state (initial load only — no data yet) */}
        {isLoading && documents.length === 0 && !error && (
          <div className="flex items-center justify-center py-12">
            <Text className="text-gray-500">Loading documents...</Text>
          </div>
        )}

        {/* Documents table */}
        {(documents.length > 0 || !isLoading) && !error && (
          <>
            <div className={`overflow-x-auto${isLoading ? ' opacity-50 pointer-events-none' : ''}`}>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    {isColumnVisible('fileName') && (
                      <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Filename</th>
                    )}
                    {isColumnVisible('fileType') && (
                      <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Type</th>
                    )}
                    {isColumnVisible('size') && (
                      <th
                        className="text-left py-3 px-4 text-gray-400 text-sm font-medium cursor-pointer hover:text-gray-200"
                        onClick={() => handleSort('fetchResult.fileSize')}
                      >
                        Size{getSortIndicator('fetchResult.fileSize')}
                      </th>
                    )}
                    {isColumnVisible('pages') && (
                      <th
                        className="text-left py-3 px-4 text-gray-400 text-sm font-medium cursor-pointer hover:text-gray-200"
                        onClick={() => handleSort('extractResult.extractedPagesCount')}
                      >
                        Pages{getSortIndicator('extractResult.extractedPagesCount')}
                      </th>
                    )}
                    {isColumnVisible('sentences') && (
                      <th
                        className="text-left py-3 px-4 text-gray-400 text-sm font-medium cursor-pointer hover:text-gray-200"
                        onClick={() => handleSort('evaluationResult.sentenceCount')}
                      >
                        Sentences{getSortIndicator('evaluationResult.sentenceCount')}
                      </th>
                    )}
                    {isColumnVisible('confidence') && (
                      <th
                        className="text-left py-3 px-4 text-gray-400 text-sm font-medium cursor-pointer hover:text-gray-200"
                        onClick={() => handleSort('evaluationResult.extractionConfidence')}
                      >
                        Confidence{getSortIndicator('evaluationResult.extractionConfidence')}
                      </th>
                    )}
                    {isColumnVisible('genre') && (
                      <th
                        className="text-left py-3 px-4 text-gray-400 text-sm font-medium cursor-pointer hover:text-gray-200"
                        onClick={() => handleSort('evaluationResult.genreLabel')}
                      >
                        Genre{getSortIndicator('evaluationResult.genreLabel')}
                      </th>
                    )}
                    {isColumnVisible('labels') && (
                      <th
                        className="text-left py-3 px-4 text-gray-400 text-sm font-medium cursor-pointer hover:text-gray-200"
                        onClick={() => handleSort('evaluationResult.harmfulCount')}
                      >
                        Labels{getSortIndicator('evaluationResult.harmfulCount')}
                      </th>
                    )}
                    {isColumnVisible('annotations') && (
                      <th
                        className="text-left py-3 px-4 text-gray-400 text-sm font-medium cursor-pointer hover:text-gray-200"
                        onClick={() => handleSort('annotationResult.highlightedCount')}
                      >
                        Annotations{getSortIndicator('annotationResult.highlightedCount')}
                      </th>
                    )}
                    {isColumnVisible('status') && (
                      <th
                        className="text-left py-3 px-4 text-gray-400 text-sm font-medium cursor-pointer hover:text-gray-200"
                        onClick={() => handleSort('status')}
                      >
                        Status{getSortIndicator('status')}
                      </th>
                    )}
                    {isColumnVisible('updated') && (
                      <th
                        className="text-left py-3 px-4 text-gray-400 text-sm font-medium cursor-pointer hover:text-gray-200"
                        onClick={() => handleSort('updatedAt')}
                      >
                        Updated{getSortIndicator('updatedAt')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {documents.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.size} className="py-12 text-center">
                        <Text className="text-gray-500">No documents found</Text>
                      </td>
                    </tr>
                  ) : (
                    documents.map((doc) => (
                      <tr
                        key={doc.documentId}
                        onClick={() => handleRowClick(doc.documentId)}
                        className="border-b border-gray-800/50 hover:bg-gray-800/50 cursor-pointer transition-colors"
                      >
                        {isColumnVisible('fileName') && (
                          <td className="py-3 px-4">
                            <Text className="text-gray-200 truncate max-w-xs" title={doc.fileName}>
                              {doc.fileName}
                            </Text>
                          </td>
                        )}
                        {isColumnVisible('fileType') && (
                          <td className="py-3 px-4">
                            <Text
                              className="text-gray-400"
                              title={doc.originalFileType || doc.mimeType || undefined}
                            >
                              {formatFileType(doc.mimeType, doc.originalFileType)}
                            </Text>
                          </td>
                        )}
                        {isColumnVisible('size') && (
                          <td className="py-3 px-4">
                            <Text className="text-gray-400">{formatFileSize(doc.fileSize)}</Text>
                          </td>
                        )}
                        {isColumnVisible('pages') && (
                          <td className="py-3 px-4">
                            <Text className="text-gray-400">{doc.pageCount ?? '--'}</Text>
                          </td>
                        )}
                        {isColumnVisible('sentences') && (
                          <td className="py-3 px-4">
                            <Text className="text-gray-400">{doc.sentenceCount ?? '--'}</Text>
                          </td>
                        )}
                        {isColumnVisible('confidence') && (
                          <td className="py-3 px-4">
                            {doc.extractionConfidence != null ? (
                              <Text
                                className={
                                  doc.extractionConfidence >= 80
                                    ? 'text-emerald-400'
                                    : doc.extractionConfidence >= 50
                                    ? 'text-amber-400'
                                    : 'text-red-400'
                                }
                              >
                                {doc.extractionConfidence.toFixed(0)}%
                              </Text>
                            ) : (
                              <Text className="text-gray-500">--</Text>
                            )}
                          </td>
                        )}
                        {isColumnVisible('genre') && (
                          <td className="py-3 px-4">
                            {doc.genreOverrideDisplayName ? (
                              <div className="flex items-center gap-1.5">
                                {doc.genreDisplayName && (
                                  <span className="line-through text-gray-500 text-xs">
                                    {doc.genreDisplayName}
                                  </span>
                                )}
                                <Badge
                                  color="amber"
                                  size="sm"
                                  title={`Overridden ${doc.genreOverrideAt ? new Date(doc.genreOverrideAt).toLocaleDateString() : ''}`}
                                >
                                  {doc.genreOverrideDisplayName}
                                </Badge>
                              </div>
                            ) : doc.genreDisplayName ? (
                              <Badge
                                color={['empty', 'error', 'unknown'].includes(doc.genreLabel || '') ? 'gray' : 'indigo'}
                                size="sm"
                              >
                                {doc.genreDisplayName}
                              </Badge>
                            ) : (
                              <Text className="text-gray-500">--</Text>
                            )}
                          </td>
                        )}
                        {isColumnVisible('labels') && (
                          <td className="py-3 px-4">
                            {doc.harmfulCount > 0 ? (
                              <span title={buildLabelTooltip(doc.labelCounts, classifierBackends, doc.harmfulCount)}>
                                <Badge color="red" size="sm">
                                  {Object.values(doc.labelCounts).reduce((sum, n) => sum + n, 0) || doc.harmfulCount} found
                                </Badge>
                              </span>
                            ) : doc.status === 'completed' ? (
                              <Badge color="emerald" size="sm">Clean</Badge>
                            ) : (
                              <Text className="text-gray-500">--</Text>
                            )}
                          </td>
                        )}
                        {isColumnVisible('annotations') && (
                          <td className="py-3 px-4">
                            {doc.filteredLabels !== null ? (
                              // Show fraction only when annotations are incomplete
                              doc.filteredLabels > 0 && (doc.annotatedLabels ?? 0) < doc.filteredLabels ? (
                                <span
                                  className="text-amber-400 cursor-help"
                                  title={`${doc.filteredLabels - (doc.annotatedLabels ?? 0)} labels missing annotations (no bounding boxes)`}
                                >
                                  {doc.annotatedLabels ?? 0}/{doc.filteredLabels}
                                </span>
                              ) : doc.filteredLabels > 0 ? (
                                // All annotations complete - just show the count
                                <Text className="text-gray-400">{doc.annotatedLabels ?? 0}</Text>
                              ) : (
                                <Text className="text-gray-500">0</Text>
                              )
                            ) : doc.highlightedCount !== null ? (
                              // Fallback for older documents without annotationCoverage
                              <Text className="text-gray-400">{doc.highlightedCount}</Text>
                            ) : doc.status === 'completed' ? (
                              <Text className="text-gray-500">0</Text>
                            ) : (
                              <Text className="text-gray-500">--</Text>
                            )}
                          </td>
                        )}
                        {isColumnVisible('status') && (
                          <td className="py-3 px-4">
                            <Badge
                              color={getStatusColor(doc.status)}
                              size="sm"
                              title={doc.status === 'skipped' ? (doc.skipReason || 'Document was skipped') : undefined}
                            >
                              {doc.status}
                            </Badge>
                          </td>
                        )}
                        {isColumnVisible('updated') && (
                          <td className="py-3 px-4">
                            <Text className="text-gray-400 text-sm">
                              {new Date(doc.updatedAt).toLocaleString()}
                            </Text>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800">
              <Text className="text-gray-400 text-sm">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, pagination.total)} of {pagination.total.toLocaleString()} documents
              </Text>
              <div className="flex items-center gap-2">
                <Button
                  size="xs"
                  variant="secondary"
                  disabled={currentPage <= 1}
                  onClick={() => updateParams({ page: String(currentPage - 1) })}
                  className="bg-gray-800 border-gray-700"
                >
                  Previous
                </Button>
                <Text className="text-gray-400 text-sm px-2">
                  Page {currentPage} of {totalPages}
                </Text>
                <Button
                  size="xs"
                  variant="secondary"
                  disabled={!pagination.hasMore}
                  onClick={() => updateParams({ page: String(currentPage + 1) })}
                  className="bg-gray-800 border-gray-700"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      <StartBatchModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        uploadOnly
      />
    </div>
  );
}
